<template>
  <div class="free-agents-page">
    <h2 class="section-title">Volní hráči</h2>
    
    <div v-if="loading" class="text-center my-4">Načítání...</div>
    
    <div v-else-if="freeAgents.length === 0" class="alert alert-info">
      Momentálně nejsou k dispozici žádní volní hráči.
    </div>
    
    <div v-else class="row">
      <div v-for="player in freeAgents" :key="player.id" class="col-md-6 col-lg-4 mb-4">
        <div class="player-card">
          <div class="player-header">
            <div class="player-info">
              <h5 class="player-name">{{ player.first_name }} {{ player.last_name }}</h5>
              <span class="player-position">{{ player.position }}</span>
              <span class="player-number" v-if="player.jersey_number">#{{ player.jersey_number }}</span>
            </div>
            <div class="player-status">
              <span :class="['status-badge', player.is_registered ? 'registered' : 'amateur']">
                {{ player.is_registered ? 'Registrovaný' : 'Neregistrovaný' }}
              </span>
            </div>
          </div>
          
          <div class="player-details">
            <div class="last-team" v-if="player.last_team_name">
              <strong>Poslední tým:</strong> 
              <div class="d-flex align-items-center mt-1">
                <img 
                  v-if="player.last_team_logo" 
                  :src="getLogoUrl(player.last_team_logo)" 
                  :alt="player.last_team_name + ' logo'" 
                  class="team-logo me-2"
                />
                <span v-else class="team-logo-placeholder me-2"></span>
                {{ player.last_team_name }}
              </div>
            </div>
            
            <div class="stats-summary">
              <div v-if="player.position === 'Brankář'" class="goalie-stats">
                <div class="stat-item">
                  <span class="stat-label">Zákroky:</span>
                  <span class="stat-value">{{ player.shots_against }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Úspěšnost:</span>
                  <span class="stat-value">{{ player.save_percentage }}%</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">GAA:</span>
                  <span class="stat-value">{{ player.gaa }}</span>
                </div>
              </div>
              <div v-else class="scoring-stats">
                <div class="stat-item">
                  <span class="stat-label">Góly:</span>
                  <span class="stat-value">{{ player.goals }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Asistence:</span>
                  <span class="stat-value">{{ player.assists }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Body:</span>
                  <span class="stat-value">{{ player.points }}</span>
                </div>
              </div>
            </div>
            
            <button 
              class="btn btn-info btn-sm mt-3" 
              @click="showPlayerStats(player)"
            >
              Zobrazit detailní statistiky
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Player Stats Modal -->
    <div class="modal fade" id="playerStatsModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Statistiky hráče - {{ selectedPlayer?.first_name }} {{ selectedPlayer?.last_name }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingStats" class="text-center my-4">
              Načítání statistik...
            </div>
            <div v-else-if="playerStats">
              <div class="player-info-section mb-4">
                <h6>Základní informace</h6>
                <div class="row">
                  <div class="col-md-6">
                    <p><strong>Pozice:</strong> {{ playerStats.player.position }}</p>
                    <p><strong>Status:</strong> {{ playerStats.player.is_registered ? 'Registrovaný' : 'Neregistrovaný' }}</p>
                  </div>
                  <div class="col-md-6">
                    <p v-if="playerStats.player.current_team_name">
                      <strong>Poslední tým:</strong> {{ playerStats.player.current_team_name }}
                    </p>
                    <p v-if="playerStats.player.jersey_number">
                      <strong>Číslo dresu:</strong> #{{ playerStats.player.jersey_number }}
                    </p>
                  </div>
                </div>
              </div>

              <div v-for="season in playerStats.seasons" :key="season.season_id" class="season-stats mb-4">
                <h6 class="season-title">{{ season.season_name }}</h6>
                <p v-if="season.teams_played_for" class="teams-played">
                  <strong>Týmy v sezóně:</strong> {{ season.teams_played_for }}
                </p>

                <!-- Scoring Stats for non-goalies -->
                <div v-if="playerStats.player.position !== 'Brankář'" class="scoring-stats-section">
                  <div class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>Góly</th>
                          <th>Asistence</th>
                          <th>Body</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{{ season.goals }}</td>
                          <td>{{ season.assists }}</td>
                          <td>{{ season.points }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Goalie Stats for goalies -->
                <div v-else class="goalie-stats-section">
                  <div class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>Střely celkem</th>
                          <th>Zákroky</th>
                          <th>Obdržené</th>
                          <th>Úspěšnost</th>
                          <th>GAA</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{{ season.shots_against }}</td>
                          <td>{{ season.saves }}</td>
                          <td>{{ season.goals_against }}</td>
                          <td>{{ season.save_percentage }}%</td>
                          <td>{{ season.gaa }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Modal } from 'bootstrap';

export default {
  name: 'FreeAgentPage',
  data() {
    return {
      freeAgents: [],
      loading: true,
      selectedPlayer: null,
      playerStats: null,
      loadingStats: false,
      statsModal: null
    }
  },
  methods: {
    async loadFreeAgents() {
      try {
        this.loading = true;
        const response = await axios.get('/api/free-agents');
        this.freeAgents = response.data;
      } catch (error) {
        console.error('Error loading free agents:', error);
      } finally {
        this.loading = false;
      }
    },
    getLogoUrl(logo) {
      if (!logo) return '';
      if (logo.startsWith('http')) return logo;
      return `${process.env.VUE_APP_API_URL}${logo}`;
    },
    async showPlayerStats(player) {
      this.selectedPlayer = player;
      this.playerStats = null;
      this.loadingStats = true;
      
      if (!this.statsModal) {
        this.statsModal = new Modal(document.getElementById('playerStatsModal'));
      }
      this.statsModal.show();
      
      try {
        const response = await axios.get(`/api/players/${player.id}/stats`);
        this.playerStats = response.data;
      } catch (error) {
        console.error('Error loading player stats:', error);
      } finally {
        this.loadingStats = false;
      }
    }
  },
  mounted() {
    this.loadFreeAgents();
  }
}
</script>

<style scoped>
.free-agents-page {
  max-width: 1200px;
  margin: 0 auto;
  background: #0a1023;
  color: #e0e6f7;
  min-height: 100vh;
  padding: 2rem 1rem 3rem;
}

.section-title {
  background: linear-gradient(135deg, #1fe0ff, #0a7cff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
}

.player-card {
  background: #151d3a;
  border-radius: 0.75rem;
  padding: 1.5rem;
  border: 1px solid rgba(31, 224, 255, 0.08);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.player-card:hover {
  transform: translateY(-4px);
  border-color: rgba(31, 224, 255, 0.2);
  box-shadow: 0 8px 24px rgba(10, 16, 35, 0.4),
              0 0 0 1px rgba(31, 224, 255, 0.05);
}

.player-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.player-name {
  color: #1fe0ff;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
}

.player-position {
  background: rgba(31, 224, 255, 0.08);
  color: #e0e6f7;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  margin-right: 0.5rem;
  border: 1px solid rgba(31, 224, 255, 0.1);
}

.player-number {
  background: linear-gradient(135deg, #1fe0ff, #0a7cff);
  color: #0a1023;
  padding: 0.25rem 0.5rem;
  border-radius: 0.4rem;
  font-size: 0.85rem;
  font-weight: 700;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-badge.registered {
  background: rgba(52, 211, 153, 0.15);
  color: #34d399;
}

.status-badge.amateur {
  background: rgba(255, 179, 71, 0.12);
  color: #ffb347;
}

.player-details {
  border-top: 1px solid rgba(31, 224, 255, 0.06);
  padding-top: 1rem;
}

.last-team {
  margin-bottom: 1rem;
}

.team-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.team-logo-placeholder {
  width: 24px;
  height: 24px;
  background: rgba(136, 146, 176, 0.15);
  border-radius: 6px;
}

.stats-summary {
  margin-bottom: 1rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: #8892b0;
  font-weight: 500;
}

.stat-value {
  color: #1fe0ff;
  font-weight: 700;
}

.btn-info {
  background: linear-gradient(135deg, #1fe0ff 0%, #0a7cff 100%);
  color: #0a1023;
  font-weight: 700;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 1.5rem;
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(31, 224, 255, 0.2);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-info:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(31, 224, 255, 0.35);
}

.modal-content {
  background: #151d3a;
  color: #e0e6f7;
  border: 1px solid rgba(31, 224, 255, 0.1);
  border-radius: 1rem;
  box-shadow: 0 16px 48px rgba(10, 16, 35, 0.7);
}

.modal-header {
  border-bottom: 1px solid rgba(31, 224, 255, 0.08);
  padding: 1.25rem 1.5rem;
}

.btn-close {
  filter: invert(1) grayscale(100%) brightness(200%);
  opacity: 0.5;
}

.btn-close:hover {
  opacity: 0.9;
}

.table {
  background: transparent;
  color: #e0e6f7;
  border-radius: 0.75rem;
  overflow: hidden;
  border-collapse: separate;
  border-spacing: 0;
}

.table thead th {
  background: rgba(31, 224, 255, 0.06);
  color: #1fe0ff;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border: none;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(31, 224, 255, 0.1);
}

.table td {
  border: none;
  vertical-align: middle;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid rgba(31, 224, 255, 0.04);
}

.table tbody tr {
  transition: background 0.2s ease;
}

.table tbody tr:hover {
  background: rgba(31, 224, 255, 0.03);
}

.alert-info {
  background: rgba(31, 224, 255, 0.08);
  color: #1fe0ff;
  border: 1px solid rgba(31, 224, 255, 0.15);
  border-radius: 0.75rem;
}

.season-title {
  color: #1fe0ff;
  font-weight: 700;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(31, 224, 255, 0.08);
}

.teams-played {
  font-size: 0.9rem;
  color: #8892b0;
  margin-bottom: 1rem;
}

.season-stats {
  background: rgba(31, 224, 255, 0.04);
  border: 1px solid rgba(31, 224, 255, 0.06);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.season-stats:last-child {
  margin-bottom: 0;
}
</style> 